import { LoadingButton } from '@mui/lab';
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { FormActionButtons } from '../../../components/Form/FormComponents';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { IFormulaFieldDataModel } from '../../../data-models/formula-field.data-model';
import {
  bulkUpdateFormulaDisplayConfig,
  COMPANY_VIEWS_COMPONENT,
  getDisplayedFormulaFieldIds,
} from '../../../services/queries/FieldDisplayConfigQueries';
import { displayedFormulaFieldIdsAtom } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { getErrorMessage } from '../../../services/queryHelpers';
import { FORMULA_DISPLAY_TARGET_LABEL } from './FormulaFieldDisplayStep';

interface BulkFormulaDisplayModalProps {
  fields: IFormulaFieldDataModel[];
  /** Registered ids, so the switch can seed from the selection's real state. */
  displayedIds: ReadonlySet<number>;
  /** Ran on a successful apply, before closing, so the grid can drop the selection. */
  onApplied?: () => void;
  onClose: () => void;
}

/**
 * Bulk "Set Field Display" for selected formula fields: the switch REPLACES
 * each field's showInGrid. Only display is bulk-editable — no other part of a
 * formula field. Like the single-field step (and unlike the KPI bulk modal's
 * reload), a list-atom refresh is enough: formula columns are FE-injected.
 */
export function BulkFormulaDisplayModal({
  fields,
  displayedIds,
  onApplied,
  onClose,
}: BulkFormulaDisplayModalProps) {
  const { pushErrorToast, pushSuccessToast, pushWarningToast } = useToastMessageState();
  const setDisplayedIds = useSetAtom(displayedFormulaFieldIdsAtom);
  // Seeded from the selection, so Apply without touching the switch is a no-op
  // rather than a silent mass show/hide. A mixed selection seeds OFF: applying
  // that hides, which is visible and reversible, where re-exposing a column
  // org-wide is not.
  const [enabled, setEnabled] = useState(() => fields.every((field) => displayedIds.has(field.id)));
  const [saving, setSaving] = useState(false);

  // The view(s) the selection spans, deduped in a stable order.
  const targetLabels = useMemo(() => {
    const labels = new Set(
      fields.map(
        (field) => FORMULA_DISPLAY_TARGET_LABEL[field.entity] ?? FORMULA_DISPLAY_TARGET_LABEL.company
      )
    );
    return Object.values(FORMULA_DISPLAY_TARGET_LABEL)
      .filter((label) => labels.has(label))
      .join(', ');
  }, [fields]);

  const onApply = async () => {
    setSaving(true);
    try {
      const result = await bulkUpdateFormulaDisplayConfig(
        fields.map((field) => field.id),
        { component: COMPANY_VIEWS_COMPONENT, displayed: enabled }
      );
      const updatedMessage = `Updated the display of ${result.updated.length} formula field${
        result.updated.length === 1 ? '' : 's'
      }.`;
      if (result.skipped.length > 0) {
        pushWarningToast({
          message: `${updatedMessage} ${result.skipped.length} could no longer be found and ${
            result.skipped.length === 1 ? 'was' : 'were'
          } skipped.`,
        });
      } else {
        pushSuccessToast({ message: updatedMessage });
      }
      setDisplayedIds(getDisplayedFormulaFieldIds());
      onApplied?.();
      onClose();
    } catch (err) {
      pushErrorToast({ message: getErrorMessage(err, 'Failed to update the field display settings.') });
      setSaving(false);
    }
  };

  return (
    <BasicDialog open title={`Edit Field Display (${fields.length} Formulas)`} onClose={onClose}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '34rem', p: '0 2.5rem 1rem' }}
      >
        <Typography variant='body2' color='text.secondary'>
          Replaces whether the selected formula fields appear on {targetLabels}. No other settings are
          changed.
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={enabled} disabled={saving} onChange={(e) => setEnabled(e.target.checked)} />
          }
          label={`Include in ${targetLabels}`}
        />
        {!enabled && (
          <Typography variant='caption' color='text.secondary'>
            Applying with this off removes the selected formula fields from {targetLabels} and their column
            pickers. The fields still evaluate, so other formulas can keep referencing them.
          </Typography>
        )}
        <FormActionButtons>
          <LoadingButton
            color='secondary'
            variant='contained'
            loading={saving}
            disabled={saving}
            onClick={onApply}
          >
            Apply
          </LoadingButton>
          <Button color='secondary' variant='outlined' disabled={saving} onClick={onClose}>
            Cancel
          </Button>
        </FormActionButtons>
      </Box>
    </BasicDialog>
  );
}
